import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import includes from 'lodash/includes';
import format from 'date-fns/format';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen } from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import { countryCode } from 'Helpers/Geo/data';
import ProductCover from 'Components/Utility/ProductCover';
import PriceContainer from 'Components/Utility/Price/PriceContainer';
import HitCardButtons from 'Components/Cards/HitCardButtons';
import HitCardWishlistBtn from 'Components/Cards/HitCardWishlistBtn';
import HitCardLabel from 'Components/Cards/HitCardLabel';
import Drm from 'Components/Utility/Drm';
import Price from 'Components/Utility/Price/Price';
import FormatProductName from 'Helpers/Format/FormatProductName';
import DlcBadge from 'Components/Cards/DlcBadge';
import 'Components/Cards/TopSellersCard/TopSellersCard.scss';

const TopSellersCard = ({
  hit,
  index,
  showReleaseDate,
  showCriticScore,
  inLibrary,
  inSteamLibrary,
  authenticated,
}) => {
  const { locale } = window;
  const {
    product_id: id,
    sdesc,
    sdescs,
    cover,
    slug,
    price,
    tiered,
    presale,
    type,
    drm,
    display_type: displayType,
    operating_systems: OS,
    best_ever: bestEver,
    release_date: releaseDate,
    giveaway,
    free_to_play: freeToPlay,
    available_valid_until: endingDate,
    hide_discount: hideDiscount,
    no_release_date: noReleaseDate,
    fullPrice,
    mystery,
    pay_what_you_want: payWhatYouWant,
    discount_percent_override: discountPercentOverride,
    preorder_playable_from: earlyAccessDate,
    star_deal: isStarDeal,
  } = hit;
  const combinedIndex = index + 1;

  let noGermanRelease = false;
  if (countryCode === 'DE' && noReleaseDate) {
    noGermanRelease = true;
  }

  const isPromotion = (type === 'pick-and-mix');

  const name = FormatProductName(hit.name, type);

  let discountPercent = hit.discount_percent;

  if (hit.type === 'pick-and-mix') {
    discountPercent = hit.pnm_saving;
  }

  let metacriticColour = 'green';
  if (hit.metacritic_score && hit.metacritic_score < 75) metacriticColour = 'yellow';
  if (hit.metacritic_score && hit.metacritic_score < 50) metacriticColour = 'red';

  let labelOverride;

  if (inLibrary && authenticated && !hit.mystery) {
    labelOverride = 'inLibrary';
  } else if (inSteamLibrary && authenticated && !hit.mystery) {
    labelOverride = 'inSteamLibrary';
  }

  return (
    <article
      className={classNames(
        'ts-item faux-block-link',
        { 'ts-item__in-library': inLibrary },
        { 'ts-item__in-steam-library': inSteamLibrary },
      )}
    >
      <div className="ts-item-wrapper d-flex flex-row">
        <div className="card-overlay">
          <div className="overlay-content">
            <div className="wishlist-container">
              {(presale && !freeToPlay) && !isPromotion &&
                <HitCardWishlistBtn productId={id} slug={slug} hit={hit} componentType="TopSellersCard" />
              }
              {!presale && !isPromotion &&
                <HitCardWishlistBtn productId={id} slug={slug} hit={hit} componentType="TopSellersCard" />
              }
            </div>
          </div>
        </div>
        {!showCriticScore &&
          <div className="ts-number align-self-center">
            {combinedIndex}
          </div>
        }

        {showCriticScore &&
          <div className={`ts-critic-score metacritic metacritic--${metacriticColour}`}>
            {hit.metacritic_score}
          </div>
        }

        <div className="ts-img-wrapper align-self-center">
          <HitCardLabel
            slug={slug}
            bestDiscount={bestEver}
            preorderDate={releaseDate}
            endingDate={endingDate}
            type={type}
            displayType={displayType}
            presale={presale}
            earlyAccessDate={earlyAccessDate}
            isStarDeal={isStarDeal}
            labelOverride={labelOverride}
          />
          <ProductCover
            cover={cover}
            alt={`Product cover for ${name}`}
            displayType={displayType}
          />
        </div>
        <div className="ts-body align-self-center">
          <h2 className="ts-title">
            <Link className="faux-block-link__overlay-link" to={`/${type}/${slug}`}>
              {name}
              <span className="text-fade" />
            </Link>
          </h2>

          {showReleaseDate &&
            <div className="ts-release-date">
              <FormattedMessage
                id="top-sellers-card.releasedate"
                defaultMessage="Release Date: {releaseDate}"
                values={{ releaseDate: format(new Date(releaseDate * 1000), 'MMMM DD, YYYY') }}
              />
            </div>
          }

          <div className="ts-desc d-none d-lg-block">
            {(sdescs ? sdescs[locale] : null) || sdesc}
          </div>
          {hit.display_type !== 'book-bundle' &&
            <div className="icons-container">
              <Drm drmType={drm[0]} />
              <div className="card-os-icons">
                {OS && includes(OS, 'windows') &&
                  <FontAwesomeIcon icon={fabFaWindows} />
                }
                {OS && includes(OS, 'mac') &&
                  <FontAwesomeIcon icon={fabFaApple} />
                }
                {OS && includes(OS, 'linux') &&
                  <FontAwesomeIcon icon={fabFaLinux} />
                }
              </div>
              {hit.display_type === 'dlc' &&
                <DlcBadge />
              }
            </div>
          }
          {hit.display_type === 'book-bundle' &&
            <div className="book-bundle">
              <FontAwesomeIcon icon={faBookOpen} />
              <FormattedMessage id="top-sellers-card.book-bundle" defaultMessage="eBook Bundle" />
            </div>
          }
        </div>
        <div className="ts-pricing align-self-center">
          <div
            className="
            d-flex
            justify-content-end
            top-seller-meta
            top-seller-meta-pricing"
          >
            {giveaway &&
              <div className="d-flex">
                <div className="was-price">
                  <Price
                    price={{ CAD: 549, EUR: 499, USD: 499, GBP: 399 }}
                  />
                </div>
                <div>
                  <FormattedMessage id="top-sellers-card.free" defaultMessage="FREE" />
                </div>
              </div>
            }
            {freeToPlay &&
              <div>Free to Play</div>
            }
            {!presale && !giveaway && !freeToPlay &&
              <PriceContainer
                topSeller
                discountPercent={discountPercent}
                discountPercentOverride={discountPercentOverride}
                fullPrice={fullPrice}
                hideDiscount={hideDiscount}
                mystery={mystery}
                payWhatYouWant={payWhatYouWant}
                price={price}
                slug={slug}
                tiered={tiered}
                type={type}
              />
            }
          </div>
          <HitCardButtons
            hit={hit}
            noGermanRelease={noGermanRelease}
            payWhatYouWant={payWhatYouWant}
          />
        </div>
      </div>
    </article>
  );
};

TopSellersCard.propTypes = {
  hit: PropTypes.shape({}),
  index: PropTypes.number,
  showReleaseDate: PropTypes.bool,
  inLibrary: PropTypes.bool,
  inSteamLibrary: PropTypes.bool,
  authenticated: PropTypes.bool,
};

TopSellersCard.defaultProps = {
  hit: {},
  index: 0,
  showReleaseDate: false,
  inLibrary: false,
  inSteamLibrary: false,
  authenticated: false,
};

const mapStateToProps = ({
  auth: { authenticated },
  userLibrary,
  steamLibrary,
}, { hit: { name } }) => ({
  inLibrary: userLibrary.includes(name),
  inSteamLibrary: steamLibrary.includes(name),
  authenticated,
});

export default connect(mapStateToProps)(TopSellersCard);
